import { Injectable } from '@angular/core';
import { SLLayer, LayerAccessLevel, LayerTypeEnum } from '../model/sl-layer';
import { EnvService } from '../env.service';
import { Decorable } from '../types/decorable';
import { SubScriptionMgr } from '../model/SubscriptionMgr';
import { TreeComponent } from '@circlon/angular-tree-component';
import { AttributeInfo } from '../types/@types/sl-v-types';
import { ILayerViewLayer } from '../model/api-services/layer-views.service';
import { layerGroup } from 'leaflet';


export interface ILayerViewLayerNode {
  id: number;
  layer: ILayerViewLayer;
  isSelected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LayerListProviderService {

  private subscriptionMgr: SubScriptionMgr = new SubScriptionMgr();
  constructor(private envService: EnvService) {

  }

  GetTreeView(layers: Array<SLLayer>, whichTree: TreeComponent) {
    this.subscriptionMgr.CancelSubscriptions();
    const treeData = [];


    for (const layer of layers) {
      if (layer.type === LayerTypeEnum.Virtual_SnapLayer && (layer as any).showInLayerList === false) {
        continue;
      }
      if (layer.parentLayer !== '') { continue; }
      treeData.push(this.MakeEntry(
        whichTree,
        layer,
        layer.identifier,
        layer.name,
        SLLayer.GetIconClass(layer),
        layer.subSLLayers,
        null,
        layer.layerOn,
        layer.enabled
      ));
    }

    return [{
      layer: null, id: 0, type: 'heading', name: 'Layers', children: treeData, icon: '', visible: true, enabled: true
    }];
  }
  MakeEntry(
    whichTree: TreeComponent,
    slLayer: SLLayer,
    xLayerId,
    xLayerName,
    xLayerIcon,
    xLayerChildren = null,
    xLayerType = null,
    vis: boolean,
    isEnabled: boolean) {
    const entry: Decorable = {
      id: xLayerId,
      name: xLayerName,
      icon: xLayerIcon,
      visible: vis,
      enabled: isEnabled,
      layer: slLayer
    };
    if (slLayer.icon === 'font-icon') {
      entry.icon = 'font-icon';
      entry.fontIcon = slLayer.fontIcon;
      entry.hasFontIcon = true;
    } else {
      entry.hasFontIcon = false;
    }
    if (entry.id === '') {
      entry.id = slLayer.id;
    }
    this.subscriptionMgr.Subscribe(slLayer.change$, () => {
      const node = whichTree.treeModel.getNodeById(slLayer.identifier);
      if (!node) return;
      node.data.name = slLayer.name;
      node.data.icon = SLLayer.GetIconClass(slLayer);
      node.data.geom = slLayer.geom;
      if (slLayer.icon === 'font-icon') {
        node.data.fontIcon = slLayer.fontIcon;
        node.data.hasFontIcon = true;
      } else {
        node.data.hasFontIcon = false;
      }
      node.data.visible = slLayer.layerOn;
      node.data.enabled = slLayer.enabled;
      if (xLayerType !== null) {
        node.data.type = slLayer.type;
      }
      whichTree.treeModel.update();


    });
    this.subscriptionMgr.Subscribe(slLayer.rendering, () => {
      const node = whichTree.treeModel.getNodeById(slLayer.identifier);
      if (!node) return;
      whichTree.treeModel.update();
    });
    this.subscriptionMgr.Subscribe(slLayer.rendered, () => {
      const node = whichTree.treeModel.getNodeById(slLayer.identifier);
      if (!node) return;
      whichTree.treeModel.update();
    });

    if (xLayerChildren !== null) {
      entry.children = [];
      for (const child of xLayerChildren) {
        entry.children.push(this.MakeEntry(
          whichTree,
          child,
          child.identifier,
          child.name,
          SLLayer.GetIconClass(child),
          child.subLayers,
          null,
          child.layer_on,
          child.enabled));
      }
    }
    if (xLayerType !== null) {
      entry.type = xLayerType;
    }
    return entry;
  }

  GetSearchableLayers(layers: Array<SLLayer>, visOnly?: boolean, _result?: Array<SLLayer>): Array<SLLayer> {
    const result: Array<SLLayer> = (!!_result) ? _result : [];
    for (const layer of layers) {
      if (layer.searchable === true) {
        if (this.GetSearchableFields(layer).length > 0) {
          if (result.indexOf(layer) < 0) {
            if (visOnly === true) {
              if (layer.layerVis === false) {
                continue;
              }
            }
            result.push(layer);
          }
        }
      }

    }
    return result;
  }
  GetSortedUniqueLayers(layers: Array<SLLayer>): Array<SLLayer> {
    const matches: Array<boolean> = [];
    const filteredLayers = layers.filter((layer: SLLayer) => {
      if (!!matches[layer.id]) return false;
      matches[layer.id] = true;
      return true;
    });

    filteredLayers.sort((layer1: SLLayer, layer2: SLLayer) => {
      const name1 = layer1.name.toLowerCase();
      const name2 = layer2.name.toLowerCase();
      if (name1 > name2) return 1;
      if (name1 === name2) {
        return (layer1.id > layer2.id) ? 1 : -1;
      }
      return -1;
    });
    return filteredLayers;
  }
  GetEditableLayers(layers: Array<SLLayer>): Array<SLLayer> {
    const result: Array<SLLayer> = [];
    for (const layer of layers) {
      if (Number(layer.access) === LayerAccessLevel.Edit) {
        result.push(layer);
      }
    }
    return this.GetSortedUniqueLayers(result);
  }
  GetHoverTipLayers(layers: Array<SLLayer>): Array<SLLayer> {
    const result: Array<SLLayer> = [];
    for (const layer of layers) {
      if (layer.tooltipOn === true) {
        if (layer.layerOn === false) {
          continue;
        }
        if (layer.tooltip === '') {
          continue;
        }
        if (result.indexOf(layer) < 0) {
          result.push(layer);
        }
      }
    }
    return result;
  }

  GetSearchableFields(layer: SLLayer): Array<AttributeInfo> {
    const fields: Array<AttributeInfo> = [];
    for (const field of layer.layerAttributes) {
      if (['1', 'true'].indexOf('' + field.searchable) > -1) {
        fields.push(field);
      }
    }
    return fields;
  }

  GetTreeViewFromViewLayers(layers: Array<ILayerViewLayer>, selDefault = false, selections: any): Array<ILayerViewLayerNode> {
    const nodes: Array<ILayerViewLayerNode> = [];

    let ctr = 0;
    for (const layerEntry of layers) {
      if (!selections.hasOwnProperty(layerEntry.id)) {
        selections[layerEntry.id] = selDefault;
      }
      const entry: ILayerViewLayerNode = {
        id: ctr,
        layer: layerEntry,
        isSelected: selections['' + layerEntry.id]
      };

      if (!(layerEntry instanceof SLLayer)) {
        if (layerEntry.geom === 'footprint') {
          layerEntry.icon = layerEntry.fontIcon;
          layerEntry.hasFontIcon = true;
        } else {
          layerEntry.icon = SLLayer.GetIconClass(layerEntry as SLLayer);
          layerEntry.hasFontIcon = false;
        }
      }
      ctr += 1;
      nodes.push(entry);
    }
    return nodes;
  }
}
